/**
 * V2 admin · Property detail.
 *
 * Tabbed layout (Apartments · Rooms · Bank · Revenue · Reminders · Docs ·
 * Notes) plus a sticky right-hand sidebar with quick-glance cards.
 * Per V2 handoff §4.2.
 */
import { useEffect, useMemo, useState } from "react";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft, EyeOff, Pencil } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { PropertySidebar } from "@/components/admin/v2/PropertySidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotesPanel } from "@/components/admin/v2/notes/NotesPanel";
import { useOpenNoteCount } from "@/hooks/v2/useEntityNotes";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLessor } from "@/hooks/v2/useLessors";
import {
  resolveEffectiveBank,
  useApartments,
  useDeleteProperty,
  useProperty,
  usePropertyBankOverride,
  usePropertyRooms,
  useUpdateProperty,
} from "@/hooks/v2/useProperties";
import { usePropertyReminderStats } from "@/hooks/v2/usePropertyReminderStats";
import { PropertyEditSheet } from "@/components/admin/v2/PropertyEditSheet";
import {
  PAYMENT_CONFIG_EDIT_PARAM,
  PAYMENT_CONFIG_EDIT_SECTION,
} from "@/lib/payment-configuration-link";
import { DetailActionsMenu } from "@/components/admin/v2/DetailActionsMenu";
import { DeleteConfirmDialog } from "@/components/admin/v2/DeleteConfirmDialog";
import { DetailPhotoGallery } from "@/components/admin/v2/DetailPhotoGallery";
import { RoomsTab } from "@/components/admin/v2/property-tabs/RoomsTab";
import { ApartmentsTab } from "@/components/admin/v2/property-tabs/ApartmentsTab";
import { PropertyGlobalSearch } from "@/components/admin/v2/property-tabs/PropertyGlobalSearch";
import { BankTab } from "@/components/admin/v2/property-tabs/BankTab";
import { RevenueTab } from "@/components/admin/v2/property-tabs/RevenueTab";
import { RemindersTab } from "@/components/admin/v2/property-tabs/RemindersTab";
import { DocsTab } from "@/components/admin/v2/property-tabs/DocsTab";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOwnerPropertyAccess } from "@/hooks/v2/useOwnerPropertyAccess";

export default function V2PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const propertyId = id ? Number(id) : null;
  const { isOwnerPortalOnly } = useAuth();
  const { data: ownerAllowed, isFetched: ownerAccessFetched } =
    useOwnerPropertyAccess(isOwnerPortalOnly ? propertyId : null);

  const {
    data: property,
    isLoading: pLoading,
    error: pError,
  } = useProperty(propertyId);
  const { data: rooms = [], isLoading: rLoading } =
    usePropertyRooms(propertyId);
  const { data: apartments = [] } = useApartments(propertyId);
  const { data: lessor } = useLessor(property?.lessor_id ?? null);
  const { data: propertyBank } = usePropertyBankOverride(propertyId);
  // Fetched at page level, not inside the tab: Radix unmounts inactive tabs, so
  // a count living in the panel would only ever appear once you were already
  // looking at it.
  const openNoteCount = useOpenNoteCount(
    propertyId === null ? null : { level: "property", propertyId }
  );

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const updateMut = useUpdateProperty();
  const deleteMut = useDeleteProperty();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (!isOwnerPortalOnly || !propertyId) return;
    if (!ownerAccessFetched) return;
    if (ownerAllowed === false) {
      navigate("/admin/properties", { replace: true });
    }
  }, [
    isOwnerPortalOnly,
    propertyId,
    ownerAccessFetched,
    ownerAllowed,
    navigate,
  ]);

  // ── URL-driven tabs ─────────────────────────────────────────────────────
  // Tab state lives in `?tab=…` so Property cards can deep-link to the
  // Reminders tab, and so reload/share keeps the tab choice.
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_VALUES = [
    "apartments",
    "rooms",
    "bank",
    "revenue",
    "reminders",
    "docs",
    "notes",
  ] as const;
  type TabValue = (typeof TAB_VALUES)[number];
  const DEFAULT_TAB: TabValue = "apartments";
  const tabParam = searchParams.get("tab") as TabValue | null;
  // `?edit=revenue` deep link (COL-287): a management-invoice row's "Payment
  // configuration" action lands here with the edit sheet open on its Revenue
  // model section. The parameter is dropped when the sheet closes so a reload
  // does not reopen it.
  const editParam = searchParams.get(PAYMENT_CONFIG_EDIT_PARAM);
  const focusSection =
    editParam === PAYMENT_CONFIG_EDIT_SECTION ? "revenue" : null;
  useEffect(() => {
    if (focusSection) setEditOpen(true);
  }, [focusSection]);
  const handleEditOpenChange = (next: boolean) => {
    setEditOpen(next);
    if (!next && editParam != null) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete(PAYMENT_CONFIG_EDIT_PARAM);
          return params;
        },
        { replace: true }
      );
    }
  };
  const activeTab: TabValue =
    tabParam && (TAB_VALUES as readonly string[]).includes(tabParam)
      ? tabParam
      : DEFAULT_TAB;
  // ── Tab switching ───────────────────────────────────────────────────────
  const handleTabChange = (next: string) => {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === DEFAULT_TAB) out.delete("tab");
        else out.set("tab", next);
        return out;
      },
      { replace: true }
    );
  };
  const TAB_LABEL: Record<TabValue, string> = {
    apartments: t("admin.field.apartments", "Apartments"),
    rooms: t("admin.section.rooms", "Rooms"),
    bank: t("admin.tab.bank", "Bank"),
    revenue: t("admin.tab.revenue", "Revenue"),
    reminders: t("admin.tab.reminders", "Reminders"),
    docs: t("admin.tab.docs", "Docs"),
    notes: t("admin.tab.notes", "Notes"),
  };

  // Reminders badge: current-period overdue rows + open past-month rows
  // (COL-435), from the shared hook so this count matches the property's
  // Reminders tab and the other property cards.
  const { data: reminderStats, isError: reminderStatsError } =
    usePropertyReminderStats(propertyId);
  const overdueCount = reminderStats?.total ?? 0;
  const inkassoCount = reminderStats?.inkasso ?? 0;

  const effectiveBank = useMemo(
    () =>
      resolveEffectiveBank({
        propertyBank: propertyBank ?? null,
        lessor: lessor ?? null,
      }),
    [propertyBank, lessor]
  );

  // ─── Loading ────────────────────────────────────────────────────────────
  if (pLoading || (propertyId && !property && !pError)) {
    return (
      <AdminV2Layout
        title={t("admin.state.loading", "Loading…")}
        breadcrumbs={[
          {
            label: t("owner.portal.breadcrumbRoot", "Admin"),
            to: "/admin",
          },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.state.loading", "Loading…") },
        ]}
      >
        <div className="space-y-4">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AdminV2Layout>
    );
  }

  // ─── Error / not found ──────────────────────────────────────────────────
  if (pError || !property) {
    return (
      <AdminV2Layout
        title={t("admin.error.propertyNotFound", "Property not found")}
        breadcrumbs={[
          {
            label: t("owner.portal.breadcrumbRoot", "Admin"),
            to: "/admin",
          },
          {
            label: t("admin.nav.properties", "Properties"),
            to: "/admin/properties",
          },
          { label: t("admin.error.propertyNotFound", "Property not found") },
        ]}
      >
        <Card className="p-8 space-y-3 max-w-lg">
          <h2 className="text-lg font-semibold">
            {t("admin.error.propertyNotFound", "Property not found")} #{id}
          </h2>
          <p className="text-sm text-muted-foreground">
            {pError instanceof Error
              ? pError.message
              : t(
                  "owner.property.notFoundHelp",
                  "This property may have been deleted or you may not have access."
                )}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/properties">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("owner.property.backToList", "Back to properties")}
            </Link>
          </Button>
        </Card>
      </AdminV2Layout>
    );
  }

  // ─── Loaded ─────────────────────────────────────────────────────────────
  function handleToggleHide() {
    // Captured before the mutation: "going live" is specifically the
    // hidden → visible transition, and property.is_hidden itself flips once
    // the mutation's optimistic/refetched state lands.
    const goingLive = property.is_hidden === true;
    updateMut.mutate(
      { id: property.id, patch: { is_hidden: !property.is_hidden } },
      {
        onSuccess: () => {
          toast({
            title: property.is_hidden ? "Property unhidden" : "Property hidden",
            description: property.is_hidden
              ? "Visible on the public properties listing."
              : "Removed from the public properties listing.",
          });
          // Owner "Portal Ready" mail — fire-and-forget, only on the first
          // hidden → visible transition. The idempotency stamp (per
          // apartment, claimed inside the function) means re-hiding and
          // unhiding again never double-sends.
          if (goingLive) {
            void supabase.functions
              .invoke("notify-owner-lifecycle", {
                body: { event: "portal_ready", propertyId: property.id },
              })
              .catch(() => {});
          }
        },
        onError: (err) =>
          toast({
            title: "Could not toggle visibility",
            description: err instanceof Error ? err.message : "Unknown error",
            variant: "destructive",
          }),
      }
    );
  }

  function handleDelete() {
    deleteMut.mutate(property.id, {
      onSuccess: () => {
        toast({
          title: "Property deleted",
          description: `${property.title} and its rooms were removed.`,
        });
        navigate("/admin/properties");
      },
      onError: (err) =>
        toast({
          title: "Could not delete property",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive",
        }),
    });
  }

  return (
    <AdminV2Layout
      title={property.title}
      titleBadge={
        property.is_hidden ? (
          <Badge variant="secondary" className="gap-1">
            <EyeOff className="h-3 w-3" />
            {t("admin.badge.hidden", "Hidden")}
          </Badge>
        ) : undefined
      }
      breadcrumbs={[
        {
          label: t("owner.portal.breadcrumbRoot", "Admin"),
          to: "/admin",
        },
        {
          label: t("admin.nav.properties", "Properties"),
          to: "/admin/properties",
        },
        // When a non-default tab is active, the property name links back to
        // the default (Apartments) view so the breadcrumb chain is "Admin ›
        // Properties › <Property> › Reminders" — matching the screenshots.
        activeTab === DEFAULT_TAB
          ? { label: property.title }
          : { label: property.title, to: `/admin/properties/${property.id}` },
        ...(activeTab === DEFAULT_TAB ? [] : [{ label: TAB_LABEL[activeTab] }]),
      ]}
      actions={
        <>
          <Button size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t("admin.action.edit", "Edit")}
          </Button>
          <DetailActionsMenu
            isHidden={!!property.is_hidden}
            onToggleHide={handleToggleHide}
            onDelete={() => setDeleteOpen(true)}
            disabled={updateMut.isPending || deleteMut.isPending}
          />
        </>
      }
      sidebar={
        <PropertySidebar
          property={property}
          rooms={rooms}
          apartments={apartments}
          propertyLessor={lessor ?? null}
        />
      }
    >
      {/* Photo gallery — read-only view of the property's images. The
          editor's primary "where are my uploads" answer. Clicks open
          a fullscreen lightbox; thumbnails switch the cover. */}
      <div className="mb-6">
        <DetailPhotoGallery
          images={property.images}
          title={property.title}
          emptyAction={
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("admin.action.uploadPhotos", "Upload photos")}
            </Button>
          }
        />
      </div>

      {/* Description — full body of the property's marketing copy. Hidden
          when not set. Renders before the tabs because it's overview
          info, not tab-specific. */}
      {property.description && property.description.trim().length > 0 && (
        <Card className="p-5 mb-6 space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {t("owner.property.aboutTitle", "About this property")}
          </h3>
          <p className="text-sm leading-relaxed whitespace-pre-line">
            {property.description}
          </p>
        </Card>
      )}

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="apartments">
            {t("admin.field.apartments", "Apartments")}
            <span className="ml-1.5 text-xs opacity-60 tabular-nums">
              ({apartments.length})
            </span>
          </TabsTrigger>
          <TabsTrigger value="rooms">
            {t("admin.section.rooms", "Rooms")}
            <span className="ml-1.5 text-xs opacity-60 tabular-nums">
              ({rooms.filter((r) => !r.is_hidden).length})
            </span>
          </TabsTrigger>
          <TabsTrigger value="bank">{t("admin.tab.bank", "Bank")}</TabsTrigger>
          <TabsTrigger value="revenue">
            {t("admin.tab.revenue", "Revenue")}
          </TabsTrigger>
          <TabsTrigger value="reminders" className="relative">
            {t("admin.tab.reminders", "Reminders")}
            {reminderStatsError && (
              <span
                className="absolute -top-1.5 -right-2 h-[16px] min-w-[16px] px-1 rounded-full bg-muted text-muted-foreground border-2 border-background text-[9px] font-bold flex items-center justify-center leading-none shadow-sm"
                title="Could not load reminders"
              >
                —
              </span>
            )}
            {!reminderStatsError && overdueCount > 0 && (
              <span
                className="absolute -top-1.5 -right-2 h-[16px] min-w-[16px] px-1 rounded-full bg-rose-500 text-white border-2 border-background text-[9px] font-bold flex items-center justify-center leading-none shadow-sm"
                title={`${overdueCount} outstanding ${overdueCount === 1 ? "reminder" : "reminders"}${inkassoCount > 0 ? ` · ${inkassoCount} inkasso` : ""}`}
                aria-label={`${overdueCount} outstanding reminders`}
              >
                {overdueCount > 99 ? "99+" : overdueCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="docs">{t("admin.tab.docs", "Docs")}</TabsTrigger>
          <TabsTrigger value="notes" className="relative">
            {t("admin.tab.notes", "Notes")}
            {openNoteCount > 0 && (
              <span
                className="absolute -top-1.5 -right-2 flex h-[16px] min-w-[16px] items-center justify-center rounded-full border-2 border-background bg-petrol px-1 text-[9px] font-bold leading-none text-white shadow-sm"
                title={`${openNoteCount} open ${openNoteCount === 1 ? "note" : "notes"}`}
                aria-label={`${openNoteCount} open notes`}
              >
                {openNoteCount > 99 ? "99+" : openNoteCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Global search — one box under the tab bar that queries every entity
            across the whole portfolio (apartments, rooms, tenants, lessors),
            routing straight to each match. Matches in this property rank first. */}
        <PropertyGlobalSearch currentPropertyId={property.id} />

        <TabsContent value="apartments">
          <ApartmentsTab propertyId={property.id} />
        </TabsContent>

        <TabsContent value="rooms">
          {rLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="p-3 flex items-center gap-4">
                  <Skeleton className="h-14 w-20 rounded-md" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-8 w-16" />
                </Card>
              ))}
            </div>
          ) : (
            <RoomsTab propertyId={property.id} rooms={rooms} />
          )}
        </TabsContent>

        <TabsContent value="bank">
          <BankTab effectiveBank={effectiveBank} />
        </TabsContent>

        <TabsContent value="revenue">
          <RevenueTab property={property} rooms={rooms} />
        </TabsContent>

        <TabsContent value="reminders">
          <RemindersTab propertyId={property.id} />
        </TabsContent>

        <TabsContent value="docs">
          <DocsTab propertyId={property.id} />
        </TabsContent>

        <TabsContent value="notes">
          <NotesPanel
            scope={{ level: "property", propertyId: property.id }}
            title={t("admin.tab.notes", "Notes")}
            emptyHint={t(
              "admin.notes.propertyHint",
              "Anything the next admin should know about this building — janitor contacts, access quirks, owner agreements, open follow-ups."
            )}
          />
        </TabsContent>
      </Tabs>

      <PropertyEditSheet
        open={editOpen}
        onOpenChange={handleEditOpenChange}
        property={property}
        bankOverride={propertyBank ?? null}
        focusSection={focusSection}
      />

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        entityLabel="property"
        entityName={property.title}
        requireTypedConfirm
        actionLabel="Delete property"
        isDeleting={deleteMut.isPending}
        onConfirm={handleDelete}
        description={
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
            <p className="font-medium text-foreground">
              {rooms.filter((r) => !r.is_hidden).length === 0
                ? "No rooms linked."
                : rooms.filter((r) => !r.is_hidden).length === 1
                  ? "1 room is linked to this property."
                  : `${rooms.filter((r) => !r.is_hidden).length} rooms are linked to this property.`}
            </p>
            <p className="text-xs text-muted-foreground">
              Linked rooms will be deleted along with the property (FK cascade).
              Existing bookings on those rooms become orphaned references —
              review tenant detail before proceeding if any are active.
            </p>
          </div>
        }
      />
    </AdminV2Layout>
  );
}
